import { Loader2 } from 'lucide-react'
import { useEffect, useRef, useCallback } from 'react'

import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '@/modules/shared/components/ui/card'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/modules/shared/components/ui/dialog'
import { ScrollArea } from '@/modules/shared/components/ui/scroll-area'
import type { Entity } from '@/modules/shared/types/entity.type'

import { useOrderFormComponentStore } from '../../../stores/order-form-component.store'
import { CustomerSearchInputComponent } from '@/modules/shared/components/client-search-input.component'
import type { PayloadSearch } from '@/modules/shared/types/payload-search.type'
import { cn } from '@/modules/shared/lib/utils'
import { formatCnpjCpf } from '@/modules/customer/helpers/format-fields-customer-form.helper'
import { useEntitySelection } from './use-entity-selection.hook'

export function ModalEntityInfiniteScrollComponent() {
	const {
		entityType,
		isOpenOrderFormModalEntity,
	} = useOrderFormComponentStore()

	const {
		entities,
		isLoading,
		isLoadingMore,
		hasMore,
		totalRecords,
		searchEntities,
		loadMore,
		closeModal,
		selectClient,
		selectTransport,
		selectSeller,
	} = useEntitySelection()

	const scrollAreaRef = useRef<HTMLDivElement>(null)
	const loadingRef = useRef<HTMLDivElement>(null)
	const initialSearchStartedRef = useRef(false)

	useEffect(() => {
		if (!isOpenOrderFormModalEntity || entityType !== 'client') {
			initialSearchStartedRef.current = false
			return
		}
		if (initialSearchStartedRef.current) return
		initialSearchStartedRef.current = true
		searchEntities('client', 'razao_social_nome', '')
	}, [isOpenOrderFormModalEntity, entityType, searchEntities])

	useEffect(() => {
		if (!hasMore || isLoadingMore) return

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					loadMore()
				}
			},
			{ threshold: 0.1 }
		)

		if (loadingRef.current) {
			observer.observe(loadingRef.current)
		}

		return () => observer.disconnect()
	}, [hasMore, isLoadingMore, loadMore])

	const handleSearch = useCallback(({ payload }: PayloadSearch) => {
		searchEntities(entityType, payload.filter, payload.inputValue)
	}, [entityType, searchEntities])

	const handleSelectEntity = useCallback((entity: Entity) => {
		if (entityType === 'client') {
			if (!entity.idTabelaPrecoVigencia || entity.flagSituacao) {
				return;
			}
			selectClient(entity)
		} else if (entityType === 'transport') {
			selectTransport(entity)
		} else {
			selectSeller(entity)
		}
	}, [entityType, selectClient, selectTransport, selectSeller])

	const getModalTitle = () => {
		switch (entityType) {
			case 'client': return 'Selecione o cliente'
			case 'transport': return 'Selecione o transportador'
			case 'seller': return 'Selecione o vendedor'
			default: return 'Selecione uma entidade'
		}
	}

	const getEmptyMessage = () => {
		switch (entityType) {
			case 'client': return 'Nenhum cliente encontrado'
			case 'transport': return 'Nenhum transportador encontrado'
			case 'seller': return 'Nenhum vendedor encontrado'
			default: return 'Nenhuma entidade encontrada'
		}
	}

	const isClientClickable = (entity: Entity) => {
		if (entityType !== 'client') return true
		return !!entity.idTabelaPrecoVigencia && !entity.flagSituacao
	}

	return (
		<Dialog open={isOpenOrderFormModalEntity} onOpenChange={closeModal}>
			<DialogContent className="bg-white max-w-2xl">
				<DialogHeader>
					<DialogTitle>{getModalTitle()}</DialogTitle>
					{totalRecords > 0 && (
						<p className="text-sm text-muted-foreground">
							{totalRecords} {totalRecords === 1 ? 'registro encontrado' : 'registros encontrados'}
						</p>
					)}
				</DialogHeader>

				<CustomerSearchInputComponent onSubmitRequest={handleSearch} />

				<ScrollArea ref={scrollAreaRef} className="h-[70vh]">
					<div className="space-y-2">
						{isLoading ? (
							<div className="flex flex-col items-center justify-center h-64">
								<Loader2 size={32} className="animate-spin text-primary" />
								<p className="pt-6">Carregando entidade(s)...</p>
							</div>
						) : entities.length > 0 ? (
							<>
								{entities.map((entity) => (
									<Card
										key={`${entity?.idEntidade}-${entity?.razaoSocialNome}`}
										className={cn(
											isClientClickable(entity)
												? 'cursor-pointer hover:bg-muted/20 bg-muted transition-colors'
												: 'bg-muted/10 cursor-default opacity-70'
										)}
										onClick={() => {
											if (isClientClickable(entity)) {
												handleSelectEntity(entity)
											}
										}}
									>
										<CardHeader className="p-3 -mb-4">
											<CardTitle className="text-sm text-balance">
												{entity?.idEntidade} - {entity?.razaoSocialNome}
											</CardTitle>
										</CardHeader>
										<CardContent className="p-3">
											<p className="text-xs text-muted-foreground">{formatCnpjCpf(entity?.cnpjCpf)}</p>
											<p className="text-xs text-muted-foreground">
												{entity?.logradouro}, {entity?.numero}
											</p>
											<p className="text-xs text-muted-foreground">
												{entity?.cidade}, {entity?.uf}
											</p>

											{entityType === 'client' && (
												(!entity.idTabelaPrecoVigencia || entity?.flagSituacao) && (
													<div className="mt-2">
														<span className="text-xs text-white rounded-md font-medium bg-red-600 px-2 py-1 inline-block">
															{!entity.idTabelaPrecoVigencia
																? 'Cliente sem tabela de preço vigente'
																: 'Cliente bloqueado para realizar vendas'}
														</span>
													</div>
												)
											)}

										</CardContent>
									</Card>
								))}

								{hasMore && (
									<div
										ref={loadingRef}
										className="flex items-center justify-center py-4"
									>
										{isLoadingMore && (
											<>
												<Loader2 size={20} className="animate-spin text-primary mr-2" />
												<span className="text-sm text-muted-foreground">
													Carregando mais...
												</span>
											</>
										)}
									</div>
								)}

								{/* Indicador de fim da lista */}
								{!hasMore && entities.length > 10 && (
									<div className="flex items-center justify-center py-4">
										<span className="text-sm text-muted-foreground">
											Todos os registros foram carregados
										</span>
									</div>
								)}
							</>
						) : (
							<div className="flex flex-col items-center justify-center h-64">
								<p className="text-gray-500 text-center">
									Lista vazia
								</p>
								<p className="text-gray-500 text-center">
									{getEmptyMessage()}
								</p>
							</div>
						)}
					</div>
				</ScrollArea>
			</DialogContent>
		</Dialog>
	)
}